/**
 * @fileoverview Input/output schemas for the short-story-extractor agent.
 *
 * This agent combines plot extraction (summary + writtenPlot) and single-chapter
 * metadata extraction (scenes, pacing, goals, POV) into a single pass for
 * short-form fiction. Mirrors the planning-side consolidation done by
 * short-story-planner-v4.
 *
 * Output schema is flat (3 levels max) for Gemini API compatibility.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { ChapterGoalsSchema, SceneImportSchema } from '@/domains/chapter-workspace/schemas';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('The full text content of the short story to analyze.'),
  storyType: z.string().optional().describe('The story type (e.g., "Flash Fiction", "Short Story"). Used to calibrate writtenPlot detail level.'),
  projectedWordCount: z.number().optional().describe('The target word count for the story. Used to calibrate writtenPlot detail level.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Combined output: plot structure + chapter metadata + scenes.
 *
 * For short-form projects, writtenPlot is the single source of truth for the
 * story's structural arc. Chapter keyEvents is derived from writtenPlot at
 * write time (not extracted independently).
 */
export const OutputSchema = z.object({
  // Plot structure (from plot-extractor-v2)
  summary: z.string().describe(
    'A concise overview of the plot (100-150 words) that weaves together the story summary, its central theme, and the main conflict.'
  ),
  writtenPlot: z.string().describe(
    'A detailed, multi-paragraph plot outline in Markdown format. ' +
    'Use ## headings for each structural phase (Initial State, Inciting Incident, Break Into Two, Midpoint, Break Into Three, Climax, Final State). ' +
    'Use **bold** for character names and key events, *italic* for emotional states. ' +
    'Separate paragraphs with double newlines. Do NOT use HTML tags. ' +
    'Write in present tense, third person. ' +
    'The narrative should be verbose and granular enough to guide subsequent prose drafting.'
  ),

  // Chapter metadata (from single-chapter-extractor-v2)
  povCharacterName: z
    .string()
    .optional()
    .describe('The name of the POV character, if identifiable from the text.'),
  povJustification: z
    .string()
    .optional()
    .describe('Explanation for why this character is the POV.'),
  chapterGoals: ChapterGoalsSchema.describe(
    'The narrative goals this story accomplishes (plot advancement, character development, stakes).'
  ),
  pacing: z
    .enum(['Slow & Reflective', 'Moderate & Developing', 'Fast & Action-Packed'])
    .describe('The overall pacing of the story.'),
  projectedWordCount: z
    .number()
    .describe('The estimated word count of the story based on analysis.'),
  scenes: z
    .array(SceneImportSchema)
    .describe('The scenes within this story, in chronological order.'),
});

export type Output = z.infer<typeof OutputSchema>;
